import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { NzDrawerComponent } from 'ng-zorro-antd/drawer';
import { Subscription } from 'rxjs';
import { EnhancedSchedulerService } from 'src/app/scheduler/services/enhanced-scheduler.service';
import { BaseService } from '../../services';
import { AppUtils } from 'src/helpers';
import { EventDrawerComponent } from 'src/app/scheduler/components/enhanced-scheduler-form/event-drawer.component';
import { SharedModule } from '../../../shared/shared.module';

@Component({
    standalone: true,
    imports: [SharedModule, EventDrawerComponent],
    selector: 'app-schedule-event-list',
    templateUrl: './schedule-event-list.component.html',
    styleUrls: ['./schedule-event-list.component.scss']
})
export class ScheduleEventListComponent implements OnInit, OnDestroy {
    @ViewChild('drawer') drawer: NzDrawerComponent;
    @ViewChild('eventDrawer', { static: false }) eventDrawerComponent: EventDrawerComponent;
    @Input() schedulerId: number;
    @Input() scheduleName: string;
    @Input() groupId: number;
    @Output() closeDrawer: EventEmitter<{ eventUpdated: boolean }> = new EventEmitter<{ eventUpdated: boolean }>();

    events: Array<any> = [];
    isLoading = false;
    isModelLoaded = false;
    showEventDrawer = false;
    selectedEvent: any = null;
    subscriptions = new Array<Subscription>();

    // Pagination
    currentPage = 1;
    pageSize = 20;
    totalEvents = 0;

    constructor(
        private enhancedSchedulerService: EnhancedSchedulerService,
        private baseService: BaseService,
        private appUtils: AppUtils
    ) {}

    ngOnInit(): void {
        this.loadEvents();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(s => s.unsubscribe());
    }

    loadEvents(): void {
        if (!this.schedulerId) {
            return;
        }

        this.isLoading = true;
        this.isModelLoaded = false;

        // Calculate offset based on current page
        const offset = (this.currentPage - 1) * this.pageSize;

        const subscription = this.enhancedSchedulerService
            .getPagedEventsBySchedulerId(this.schedulerId, this.currentPage, this.pageSize)
            .subscribe({
                next: (response: any) => {
                    this.events = response.items || response.data || response || [];
                    this.totalEvents = response.totalCount || response.total || this.events.length;
                    this.isModelLoaded = true;
                    this.isLoading = false;
                },
                error: (error) => {
                    console.error('Failed to load events:', error);
                    this.baseService.processErrorResponse(error);
                    this.isLoading = false;
                    this.isModelLoaded = true;
                }
            });

        this.subscriptions.push(subscription);
    }

    onPageChange(page: number): void {
        this.currentPage = page;
        this.loadEvents();
    }

    openEventDrawer(event?: any): void {
        this.selectedEvent = event || null;
        this.showEventDrawer = true;
        
        // Open the inner drawer first
        setTimeout(() => {
            this.drawer?.open();
            
            // Then wait a bit more for the EventDrawerComponent to be ready
            setTimeout(() => {
                if (this.eventDrawerComponent) {
                    if (event) {
                        // Editing existing event
                        console.log('Setting event data for edit:', event);
                        this.eventDrawerComponent.setEventData(event);
                    } else {
                        // Creating new event - set default date
                        console.log('Creating new event');
                        const now = new Date();
                        const endDate = new Date(now.getTime() + 60 * 60 * 1000); // 1 hour later
                        this.eventDrawerComponent.setSelectedDate(now, endDate);
                    }
                } else {
                    console.error('EventDrawerComponent not found');
                }
            }, 100);
        }, 10);
    }

    closeEventDrawer(eventData?: any): void {
        this.showEventDrawer = false;
        this.selectedEvent = null;
        
        // Close the inner drawer
        this.drawer?.close();
        
        // Reload events if an event was added or updated
        if (eventData && (eventData.eventAdded || eventData.eventUpdated)) {
            this.loadEvents();
        }
    }

    deleteEvent(event: any): void {
        // TODO: Implement delete confirmation and API call
        console.log('Delete event:', event);
    }

    close(): void {
        this.closeDrawer.emit({ eventUpdated: false });
    }

    cancel(): void {
        this.closeDrawer.emit({ eventUpdated: false });
    }

    trackByEventId(index: number, item: any): number {
        return item.id;
    }

    formatDate(date: string): string {
        if (!date) return '';
        const d = new Date(date);
        return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }

    formatRecurrence(event: any): string {
        if (event.eventType === 1 || !event.cronExpression) {
            return 'One-time';
        }
        
        try {
            const properties = event.cronExpressionProperties ? JSON.parse(event.cronExpressionProperties) : {};
            const type = properties.type || 'custom';
            
            switch (type) {
                case 'daily':
                    return 'Daily';
                case 'weekly':
                    return 'Weekly';
                case 'monthly':
                    return 'Monthly';
                case 'yearly':
                    return 'Yearly';
                default:
                    return 'Recurring';
            }
        } catch (e) {
            return 'Recurring';
        }
    }

    getEventTypeBadge(event: any): string {
        if (event.userMediaAppId && event.userMediaAppId > 0) {
            return 'Media';
        } else if (event.displayListId && event.displayListId > 0) {
            return 'Playlist';
        }
        return 'Unknown';
    }

    getEventTypeBadgeColor(event: any): string {
        if (event.userMediaAppId && event.userMediaAppId > 0) {
            return 'blue';
        } else if (event.displayListId && event.displayListId > 0) {
            return 'green';
        }
        return 'default';
    }
}
